import { useAuth } from '@teko/auth-react';
import {
  BaseProps,
  Body,
  Button,
  CustomLink,
  Dropdown,
  Flex,
  Image,
  ListContent,
  Shape,
  Skeleton,
  styled,
  useModal,
} from '@teko/ds-desktop';
import {
  AnonymousUserProfileOutlined,
  UserProfileColored,
  UserProfileFilled,
} from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { Profile } from '@teko/user';
import { useServices, useTenantConfig } from 'contexts';
import { onSignOut } from 'helpers';
import { t } from 'i18n';
import { useState } from 'react';
import { NavigationIconButton } from './style';

interface UserProfileHeaderV2Props {
  profileLinks?: ProfileLinkProps[];
}

export const UserProfileHeaderV2: React.FunctionComponent<
  UserProfileHeaderV2Props
> = ({ profileLinks }: UserProfileHeaderV2Props) => {
  const [show, setShow] = useState(false);
  const { user: userProfile, loading: profileLoading, authService } = useAuth();
  const onLogin = () => authService.login();
  if (profileLoading) return <Skeleton height="0.75rem" width="90px" />;
  if (!userProfile?.userId) {
    return (
      <Dropdown
        placement="bottom-start"
        menu={
          <Shape style={{ cursor: 'pointer' }} onClick={onLogin}>
            <Body color="white">{t('Đăng ký/Đăng nhập')}</Body>
          </Shape>
        }
        backgroundColor="textPrimary"
        width="max-content"
        padding={['s4', 's8']}
        borderRadius="m"
      >
        <NavigationIconButton
          onClick={onLogin}
          className="button"
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'loginIcon',
          })}
        >
          <AnonymousUserProfileOutlined size={20} />
        </NavigationIconButton>
      </Dropdown>
    );
  }
  return (
    <Shape
      style={{ position: 'relative' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <CustomLink href="/account">
        <NavigationIconButton
          className="button"
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'profileIcon',
          })}
        >
          <UserProfileColored size={20} />
        </NavigationIconButton>
      </CustomLink>
      <UserProfileMenuWrapper
        style={{ position: 'absolute', transition: 'display 0.3s' }}
        padding="s4"
        borderRadius="m"
        show={show}
        backgroundColor="white"
      >
        <UserProfileMenu
          profileLinks={profileLinks}
          userProfile={userProfile}
        />
      </UserProfileMenuWrapper>
    </Shape>
  );
};

const UserProfileMenu = ({
  profileLinks,
  userProfile,
}: {
  profileLinks?: ProfileLinkProps[];
  userProfile: Profile;
}) => {
  const { showModal } = useModal();
  const { authService } = useAuth();
  const { notificationService } = useServices();
  const { notificationAppId } = useTenantConfig();
  return (
    <Shape width="240px" borderRadius="s" padding={['s12', 's4']}>
      <Flex padding="s16" gap="s12" align="center">
        <Flex
          height="36px"
          width="36px"
          align="center"
          justify="center"
          backgroundColor="tableHeader"
          style={{ borderRadius: 'calc(infinity * 1px)' }}
        >
          {userProfile.avatar ? (
            <Image
              src={userProfile.avatar}
              borderRadius="calc(infinity * 1px)"
              alt="Avatar"
            />
          ) : (
            <UserProfileFilled size={20} color="textSecondary" />
          )}
        </Flex>
        <UserInfo>
          <h3 className="name">{userProfile.name}</h3>
          <h5 className="extra">
            {userProfile.email || userProfile.telephone}
          </h5>
        </UserInfo>
      </Flex>
      {profileLinks &&
        profileLinks.map((profileLink: ProfileLinkProps) => (
          <ProfileLink
            key={profileLink.href}
            icon={profileLink.icon}
            href={profileLink.href}
            text={profileLink.text}
            contentName={profileLink.contentName}
            regionName={profileLink.regionName}
          />
        ))}
      <Shape
        padding="s8"
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'logOut',
        })}
      >
        <StyledButton
          onClick={() => {
            showModal?.({
              title: t('Đăng xuất'),
              children: (
                <div>{t('Bạn có chắc chắn muốn đăng xuất không?')}</div>
              ),
              onOk: () =>
                onSignOut(
                  authService,
                  notificationService,
                  notificationAppId,
                  window.location.origin,
                ),
              cancelText: t('Không'),
              okText: t('Có'),
            });
          }}
          variant="primary"
        >
          {t('Đăng xuất')}
        </StyledButton>
      </Shape>
    </Shape>
  );
};

interface ProfileLinkProps {
  icon: React.ReactElement;
  href: string;
  regionName: string;
  contentName: string;
  text: string;
  onClick?: () => void;
}
const ProfileLink: React.FC<ProfileLinkProps> = ({
  icon,
  text,
  href,
  regionName,
  contentName,
  onClick,
}: ProfileLinkProps) => {
  return (
    <CustomLink href={href}>
      <ListContent
        padding={['s8', 's8']}
        icon={icon}
        label={text}
        onClick={onClick}
        {...getTrackingData({
          isTrackContent: true,
          regionName,
          contentName,
        })}
      />
    </CustomLink>
  );
};

const StyledButton = styled(Button)`
  height: 2rem;
  width: 100%;
`;

const UserInfo = styled(Shape)`
  .name {
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
  }
  .extra {
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
  }
`;

interface UserProfileMenuWrapperProps extends BaseProps {
  show: boolean;
}

const UserProfileMenuWrapper = styled(Shape)<UserProfileMenuWrapperProps>`
  z-index: 100;
  display: ${(props) => (props.show ? 'block' : 'none')};
`;
